import React, {useMemo, useState} from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import ChessRestClient from '@/Api/ChessRestClient';

type SessionOption = {
  id: number;
  session_datetime: string;
  end_time?: string | null;
  topic?: string;
  coach_name?: string;
  session_number?: number;
  duration_in_mins?: number;
};

type CoachOption = {
  id: number;
  name: string;
  display_name: string;
  has_zoom_id?: boolean;
};

const ManualSubstituteAssignment = () => {
  const [batchCode, setBatchCode] = useState('');
  const [sessions, setSessions] = useState<SessionOption[]>([]);
  const [coaches, setCoaches] = useState<CoachOption[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(
    null,
  );
  const [selectedCoachId, setSelectedCoachId] = useState<number | null>(null);
  const [coachSearch, setCoachSearch] = useState('');
  const [contextLoading, setContextLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    tone: 'success' | 'error';
    message: string;
  } | null>(null);

  const selectedSession = useMemo(
    () => sessions.find(session => session.id === selectedSessionId) || null,
    [selectedSessionId, sessions],
  );
  const selectedCoach = useMemo(
    () => coaches.find(coach => coach.id === selectedCoachId) || null,
    [coaches, selectedCoachId],
  );
  const filteredCoaches = useMemo(() => {
    const query = coachSearch.trim().toLowerCase();
    if (!query) {
      return coaches.slice(0, 80);
    }
    return coaches
      .filter(coach =>
        `${coach.display_name} ${coach.name}`.toLowerCase().includes(query),
      )
      .slice(0, 80);
  }, [coachSearch, coaches]);

  const canSubmit = Boolean(selectedSession && selectedCoach);

  const resetSelections = () => {
    setSessions([]);
    setCoaches([]);
    setSelectedSessionId(null);
    setSelectedCoachId(null);
    setCoachSearch('');
  };

  const loadContext = async () => {
    const trimmedBatchCode = batchCode.trim();
    if (!trimmedBatchCode || contextLoading) {
      return;
    }

    setContextLoading(true);
    setResult(null);
    resetSelections();
    try {
      const response = await ChessRestClient.get(
        '/subscription/v1/csoc/manual-substitute-context',
        {params: {batch_code: trimmedBatchCode}},
      );
      setSessions(response.data?.sessions || []);
      setCoaches(response.data?.coaches || []);
      if (!(response.data?.sessions || []).length) {
        setResult({
          tone: 'error',
          message: 'No upcoming sessions found for this batch.',
        });
      }
    } catch (error: any) {
      setResult({
        tone: 'error',
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Unable to load sessions and coaches.',
      });
    } finally {
      setContextLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit || submitting || !selectedSession || !selectedCoach) {
      return;
    }

    setSubmitting(true);
    setResult(null);
    try {
      const response = await ChessRestClient.post(
        '/subscription/v1/csoc/manual-substitute-assignment',
        {
          batch_code: batchCode.trim(),
          session_datetime: selectedSession.session_datetime,
          new_coach_name: selectedCoach.name,
        },
      );

      setResult({
        tone: response.data?.success ? 'success' : 'error',
        message: response.data?.message || 'Substitute assignment submitted.',
      });
      if (response.data?.success) {
        resetSelections();
        setBatchCode('');
      }
    } catch (error: any) {
      setResult({
        tone: 'error',
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Unable to assign substitute coach.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <View style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.eyebrow}>CSOC Operations</Text>
        <Text style={styles.title}>Assign Substitute Coach</Text>
        <Text style={styles.subtitle}>
          Load upcoming sessions from cc_webinar_details, select the session,
          select an active CSOC coach, then assign through the existing
          notification flow.
        </Text>
      </View>

      <View style={styles.panel}>
        <Text style={styles.label}>Batch code</Text>
        <View style={styles.batchRow}>
          <TextInput
            style={styles.batchInput}
            value={batchCode}
            placeholder="e.g. ZOOM-BATCH-101"
            placeholderTextColor="#8a93a3"
            autoCapitalize="none"
            autoCorrect={false}
            onChangeText={value => {
              setBatchCode(value);
              setResult(null);
            }}
            onSubmitEditing={loadContext}
          />
          <TouchableOpacity
            style={[
              styles.loadButton,
              (!batchCode.trim() || contextLoading) && styles.disabledButton,
            ]}
            disabled={!batchCode.trim() || contextLoading}
            onPress={loadContext}>
            {contextLoading ? (
              <ActivityIndicator color="#ffffff" />
            ) : (
              <Text style={styles.buttonText}>Load</Text>
            )}
          </TouchableOpacity>
        </View>
      </View>

      {sessions.length ? (
        <View style={styles.panel}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Upcoming Sessions</Text>
            <Text style={styles.muted}>{sessions.length} found</Text>
          </View>
          <ScrollView style={styles.list} nestedScrollEnabled>
            {sessions.map(session => {
              const selected = session.id === selectedSessionId;
              return (
                <TouchableOpacity
                  key={session.id}
                  style={[styles.optionCard, selected && styles.selectedCard]}
                  onPress={() => setSelectedSessionId(session.id)}>
                  <Text style={styles.optionTitle}>
                    {formatDateTime(session.session_datetime)}
                  </Text>
                  <Text style={styles.optionMeta}>
                    {session.topic || 'Group Session'}
                  </Text>
                  <Text style={styles.optionMeta}>
                    Coach: {session.coach_name || 'Not set'} | Session{' '}
                    {session.session_number || '-'} |{' '}
                    {session.duration_in_mins || 0} min
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
      ) : null}

      {coaches.length ? (
        <View style={styles.panel}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Substitute Coach</Text>
            <Text style={styles.muted}>{coaches.length} active</Text>
          </View>
          <TextInput
            style={styles.searchInput}
            value={coachSearch}
            placeholder="Search coach name"
            placeholderTextColor="#8a93a3"
            autoCapitalize="none"
            autoCorrect={false}
            onChangeText={setCoachSearch}
          />
          <ScrollView style={styles.list} nestedScrollEnabled>
            {filteredCoaches.map(coach => {
              const selected = coach.id === selectedCoachId;
              return (
                <TouchableOpacity
                  key={coach.id}
                  style={[styles.optionCard, selected && styles.selectedCard]}
                  onPress={() => setSelectedCoachId(coach.id)}>
                  <Text style={styles.optionTitle}>{coach.display_name}</Text>
                  <Text style={styles.optionMeta}>
                    {coach.has_zoom_id
                      ? 'Zoom ID available'
                      : 'Zoom ID missing'}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
      ) : null}

      {result ? (
        <Text
          style={[
            styles.result,
            result.tone === 'success' ? styles.success : styles.error,
          ]}>
          {result.message}
        </Text>
      ) : null}

      <TouchableOpacity
        style={[
          styles.submitButton,
          (!canSubmit || submitting) && styles.disabledButton,
        ]}
        disabled={!canSubmit || submitting}
        onPress={handleSubmit}>
        {submitting ? (
          <ActivityIndicator color="#ffffff" />
        ) : (
          <Text style={styles.buttonText}>Assign Substitute</Text>
        )}
      </TouchableOpacity>
    </View>
  );
};

const formatDateTime = (value: string) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: '#f6f7fb',
    padding: 24,
  },
  header: {
    maxWidth: 840,
    marginBottom: 20,
  },
  eyebrow: {
    color: '#456ad8',
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  title: {
    color: '#111827',
    fontSize: 28,
    fontWeight: '800',
    marginTop: 6,
  },
  subtitle: {
    color: '#4b5563',
    fontSize: 15,
    lineHeight: 22,
    marginTop: 8,
  },
  panel: {
    backgroundColor: '#ffffff',
    borderColor: '#e5e7eb',
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 16,
    maxWidth: 840,
    padding: 18,
  },
  label: {
    color: '#1f2937',
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 8,
  },
  batchRow: {
    flexDirection: 'row',
    gap: 12,
  },
  batchInput: {
    borderColor: '#cfd6e4',
    borderRadius: 6,
    borderWidth: 1,
    color: '#111827',
    flex: 1,
    fontSize: 15,
    minHeight: 44,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  searchInput: {
    borderColor: '#cfd6e4',
    borderRadius: 6,
    borderWidth: 1,
    color: '#111827',
    fontSize: 15,
    marginBottom: 12,
    minHeight: 42,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  loadButton: {
    alignItems: 'center',
    backgroundColor: '#2563eb',
    borderRadius: 6,
    justifyContent: 'center',
    minHeight: 44,
    minWidth: 110,
    paddingHorizontal: 18,
  },
  sectionHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  sectionTitle: {
    color: '#111827',
    fontSize: 17,
    fontWeight: '800',
  },
  muted: {
    color: '#64748b',
    fontSize: 13,
    fontWeight: '700',
  },
  list: {
    maxHeight: 280,
  },
  optionCard: {
    borderColor: '#e5e7eb',
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 10,
    padding: 12,
  },
  selectedCard: {
    backgroundColor: '#eef4ff',
    borderColor: '#2563eb',
  },
  optionTitle: {
    color: '#111827',
    fontSize: 15,
    fontWeight: '800',
  },
  optionMeta: {
    color: '#4b5563',
    fontSize: 13,
    marginTop: 4,
  },
  result: {
    borderRadius: 6,
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 16,
    maxWidth: 840,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  success: {
    backgroundColor: '#e9f8ef',
    color: '#17653a',
  },
  error: {
    backgroundColor: '#fff1f2',
    color: '#be123c',
  },
  submitButton: {
    alignItems: 'center',
    backgroundColor: '#1d4ed8',
    borderRadius: 6,
    justifyContent: 'center',
    maxWidth: 840,
    minHeight: 46,
    paddingHorizontal: 18,
  },
  disabledButton: {
    backgroundColor: '#94a3b8',
  },
  buttonText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: '800',
  },
});

export default ManualSubstituteAssignment;
